/**
 * TriggerRulesForRepo — lists trigger rules for a specific repo and
 * provides add/edit/delete affordances.
 *
 * Rendered inside the GitHub provider block below each repo row
 * (Step 01 of the mockup, ADR 0018). This component is provider-
 * agnostic: it accepts the provider as a prop and passes it through
 * to TriggerRuleEditorModal. When a GitLab provider block lands later
 * it can reuse this component verbatim — no changes here required.
 *
 * Layer: `pages/WorkspaceSettings/` alongside GitHubIntegrationBlock
 * (not in `components/` because it's workspace-settings-specific).
 */
import { useState, useEffect, useCallback } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { TriggerRuleEditorModal } from '@/components/TriggerRuleEditorModal'
import { useConfirm } from '@/hooks/useConfirm'
import { useToast } from '@/hooks/useToast'
import { toUserMessage, extractRawDetails } from '@/lib/userMessage'
import { triggerRulesAPI } from '@/lib/api'
import { eventPillClass, eventPillLabel, ruleEventDescription } from '@/lib/triggerRules'
import type { TriggerRule, TriggerRuleProvider } from '@/types'

// ── Props ─────────────────────────────────────────────────────────────────

interface TriggerRulesForRepoProps {
  /** The provider for this repo (determines event vocabulary). */
  provider: TriggerRuleProvider
  /** The integrationId (FK to the CiIntegration record). */
  integrationId: string
  /** Provider-canonical repository identifier (e.g. "owner/repo"). */
  repoIdentifier: string
  /** Optional template name lookup for display (keyed by templateId). */
  templateNames?: Record<string, string>
}

// ── Component ─────────────────────────────────────────────────────────────

export function TriggerRulesForRepo({
  provider,
  integrationId,
  repoIdentifier,
  templateNames = {},
}: TriggerRulesForRepoProps) {
  // #1148: shared themed confirm, replacing native window.confirm().
  const confirm = useConfirm()
  // #1149: shared themed toast, replacing native window.alert().
  const toast = useToast()
  const [rules, setRules] = useState<TriggerRule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal state — null = closed, undefined = new rule (no preset rule),
  // TriggerRule = editing an existing rule
  const [editingRule, setEditingRule] = useState<TriggerRule | null | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await triggerRulesAPI.listForRepo(provider, repoIdentifier)
      setRules(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trigger rules')
    } finally {
      setLoading(false)
    }
  }, [provider, repoIdentifier])

  useEffect(() => {
    void load()
  }, [load])

  function openAddModal() {
    setEditingRule(null)
    setIsModalOpen(true)
  }

  function openEditModal(rule: TriggerRule) {
    setEditingRule(rule)
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
    setEditingRule(undefined)
  }

  function handleSaved(saved: TriggerRule) {
    closeModal()
    // Optimistic update only — avoids a redundant refetch and racey
    // double-update (background load could overwrite the optimistic state
    // or cause ordering flicker). The list is small and always sorted
    // server-side by updatedAt, so an insert-at-front is fine.
    setRules((prev) => {
      const idx = prev.findIndex((r) => r.id === saved.id)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = saved
        return next
      }
      return [saved, ...prev]
    })
  }

  async function handleDelete(rule: TriggerRule) {
    const ok = await confirm({
      title: `Delete this trigger rule for ${repoIdentifier}?`,
      description: 'The rule will no longer fire on matching repository events.',
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (!ok) return
    setDeletingId(rule.id)
    try {
      await triggerRulesAPI.delete(rule.id)
      setRules((prev) => prev.filter((r) => r.id !== rule.id))
    } catch (err) {
      toast({
        title: 'Failed to delete trigger rule',
        description: toUserMessage(
          err,
          "Couldn't delete the trigger rule. Try again — if the problem continues, check your connection.",
        ),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
    } finally {
      setDeletingId(null)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="pl-10 pr-3.5 pb-3 pt-1">
      {loading && (
        <div className="space-y-1.5 py-1">
          {[1, 2].map((i) => (
            <div key={i} className="h-8 rounded bg-muted animate-pulse" />
          ))}
        </div>
      )}

      {!loading && error && (
        <p className="text-xs text-destructive py-1">{error}</p>
      )}

      {!loading && !error && rules.length === 0 && (
        <p className="text-[11px] text-muted-foreground py-1">No triggers configured.</p>
      )}

      {!loading && !error && rules.length > 0 && (
        <ul className="space-y-0.5">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className="grid items-center gap-3 rounded px-2 py-1.5 text-xs hover:bg-muted/40 transition-colors"
              style={{ gridTemplateColumns: 'auto 1fr auto' }}
            >
              {/* Event pill */}
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${eventPillClass(rule)}`}
              >
                {eventPillLabel(rule)}
              </span>

              {/* Description */}
              <span className="text-xs text-foreground truncate">
                {ruleEventDescription(rule)}{' '}
                {templateNames[rule.templateId] ? (
                  <>
                    · run{' '}
                    <span className="text-primary font-medium">{templateNames[rule.templateId]}</span>
                  </>
                ) : null}
              </span>

              {/* Actions */}
              <span className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => openEditModal(rule)}
                  title="Edit"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void handleDelete(rule)}
                  disabled={deletingId === rule.id}
                  title="Delete"
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Add trigger rule link */}
      <button
        type="button"
        onClick={openAddModal}
        className="mt-1 inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-primary hover:bg-primary/5 transition-colors"
      >
        + Add trigger rule
      </button>

      {/* Editor modal */}
      {isModalOpen && (
        <TriggerRuleEditorModal
          initialRule={editingRule ?? undefined}
          presetRepo={
            editingRule === null
              ? { provider, integrationId, repoIdentifier }
              : undefined
          }
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
